/**
 * Declarative sound recipes for Web Audio synthesis.
 *
 * Each cue is a list of tone/noise layers with envelopes (and optional
 * shimmer). Inspired by Cuelume's recipe model — short, synthesized UI
 * feedback with no audio files.
 */

type BaseLayer = {
  /** Seconds after the trigger that this layer starts. */
  offset?: number;
  /** Fade-in time, in seconds. */
  attack: number;
  /** Hold peak level before decaying, in seconds. */
  sustain?: number;
  /** Fade-out time, in seconds, starting after attack (+ sustain). */
  decay: number;
  /** Peak volume reached at the end of the attack. */
  peak: number;
};

/** A single oscillator note — chimes, beeps, glides. */
export type ToneLayer = BaseLayer & {
  kind: "tone";
  waveform: OscillatorType;
  frequency: number;
  /** Detune in cents, for a gentle chorus between layers. */
  detune?: number;
  /** If set, pitch glides from `frequency` to this value. */
  glideTo?: number;
  /** Glide duration in seconds. Defaults to attack + decay. */
  glideTime?: number;
};

/** Filtered noise — knocks, ticks, breathy texture. */
export type NoiseLayer = BaseLayer & {
  kind: "noise";
  filterType: BiquadFilterType;
  filterFrequency: number;
  filterQ?: number;
};

export type SoundLayer = ToneLayer | NoiseLayer;

/** Soft echo tail applied to the whole cue. */
export type Shimmer = {
  delay: number;
  feedback: number;
  wet: number;
  lowpass: number;
};

export type SoundRecipe = {
  masterGain: number;
  layers: SoundLayer[];
  shimmer?: Shimmer;
  /**
   * Approximate one-shot length used when spacing looped plays
   * (e.g. the end-of-set alarm beep).
   */
  durationMs: number;
};

export const RECIPES = {
  /**
   * Check-in ring — struck bell: hard onset, then a long ringing decay.
   * Pitch shape from the old registered.mp3 (C5/E5 into G5 + fifth).
   */
  registered: {
    masterGain: 1,
    durationMs: 2800,
    layers: [
      // Strike transient
      {
        kind: "noise",
        filterType: "bandpass",
        filterFrequency: 6000,
        filterQ: 2.4,
        attack: 0.001,
        decay: 0.025,
        peak: 0.4,
      },
      // Peakier high ping on the strike
      {
        kind: "tone",
        waveform: "sine",
        frequency: 5274,
        attack: 0.001,
        decay: 0.12,
        peak: 0.4,
      },
      {
        kind: "tone",
        waveform: "sine",
        frequency: 6272,
        attack: 0.001,
        decay: 0.08,
        peak: 0.26,
      },
      // Lower partials fade sooner (bell-like)
      {
        kind: "tone",
        waveform: "sine",
        frequency: 523,
        attack: 0.002,
        decay: 1.1,
        peak: 0.3,
      },
      {
        kind: "tone",
        waveform: "sine",
        frequency: 659,
        attack: 0.002,
        decay: 1.4,
        peak: 0.2,
      },
      // Fundamental keeps ringing
      {
        kind: "tone",
        waveform: "sine",
        frequency: 785,
        attack: 0.003,
        decay: 2.6,
        peak: 0.42,
      },
      // Tiny detune for natural beating as it rings
      {
        kind: "tone",
        waveform: "sine",
        frequency: 785,
        detune: 5,
        attack: 0.003,
        decay: 2.7,
        peak: 0.22,
      },
      // Upper fifth dies off sooner than the body
      {
        kind: "tone",
        waveform: "sine",
        frequency: 1175,
        attack: 0.003,
        decay: 1.8,
        peak: 0.18,
      },
    ],
    shimmer: { delay: 0.18, feedback: 0.42, wet: 0.24, lowpass: 3800 },
  },

  /** Soft refusal — “chuchu” / “churchur”: two short ch-bursts with a muted body. */
  nope: {
    masterGain: 1,
    durationMs: 420,
    layers: [
      // First "chu"
      {
        kind: "noise",
        filterType: "bandpass",
        filterFrequency: 2200,
        filterQ: 1.5,
        attack: 0.001,
        decay: 0.045,
        peak: 0.85,
      },
      {
        kind: "tone",
        waveform: "triangle",
        frequency: 220,
        attack: 0.006,
        decay: 0.16,
        peak: 0.72,
      },
      {
        kind: "tone",
        waveform: "sine",
        frequency: 280,
        attack: 0.005,
        decay: 0.14,
        peak: 0.45,
      },
      {
        kind: "tone",
        waveform: "sine",
        frequency: 420,
        attack: 0.004,
        decay: 0.1,
        peak: 0.28,
      },
      // Second "chu" — clearer hummed note after the ch
      {
        kind: "noise",
        filterType: "bandpass",
        filterFrequency: 2000,
        filterQ: 1.4,
        offset: 0.14,
        attack: 0.001,
        decay: 0.04,
        peak: 0.6,
      },
      {
        kind: "tone",
        waveform: "sine",
        frequency: 185,
        offset: 0.15,
        attack: 0.02,
        sustain: 0.12,
        decay: 0.18,
        peak: 0.72,
      },
      {
        kind: "tone",
        waveform: "sine",
        frequency: 185,
        detune: 4,
        offset: 0.15,
        attack: 0.02,
        sustain: 0.12,
        decay: 0.2,
        peak: 0.36,
      },
      {
        kind: "tone",
        waveform: "sine",
        frequency: 370,
        offset: 0.15,
        attack: 0.015,
        sustain: 0.08,
        decay: 0.14,
        peak: 0.2,
      },
      {
        kind: "tone",
        waveform: "triangle",
        frequency: 123,
        offset: 0.15,
        attack: 0.025,
        sustain: 0.1,
        decay: 0.16,
        peak: 0.38,
      },
    ],
  },

  /**
   * Set-end beep — matched to the old alarm.mp3:
   * ~70ms chirp near 3740 Hz, one pulse per second.
   */
  alarm: {
    masterGain: 1,
    durationMs: 1000,
    layers: [
      {
        kind: "tone",
        waveform: "sine",
        frequency: 3740,
        attack: 0.005,
        sustain: 0.035,
        decay: 0.03,
        peak: 0.85,
      },
      // Slight width so it isn’t a pure test tone
      {
        kind: "tone",
        waveform: "sine",
        frequency: 3740,
        detune: 4,
        attack: 0.005,
        sustain: 0.035,
        decay: 0.03,
        peak: 0.35,
      },
    ],
  },
} as const satisfies Record<string, SoundRecipe>;

export type SoundName = keyof typeof RECIPES;

export function isSoundName(value: unknown): value is SoundName {
  return typeof value === "string" && Object.hasOwn(RECIPES, value);
}
